import { useEffect, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useAppDispatch, useAppSelector } from "app/store/hook";
import { openModal } from "app/reducers/modalSlice";
import AddSche from "./popup/AddSche";
import { SchdService } from "../../../services/wp/SchdService";
import moment from "moment";
import { Link } from "react-router-dom";
import DashboardLoading from "./DashboardLoading";
import DashboardCard from "./DashboardCard";
import {DashboardModuleProps} from "../../../model/db/dashboardModuleProps";

const koreanDays = ['일', '월', '화', '수', '목', '금', '토'];

export default function Schedule(props: Readonly<DashboardModuleProps>) {
  const [events, setEvents] = useState<any[]>([]);
  const [selectedEvents, setSelectedEvents] = useState<any>([]);
  const [selectedDate, setSelectedDate] = useState<any>(null);
  const [cellDate, setCellDate] = useState<any>(convertDate(new Date()));
  const [calendarApi, setCalendarApi] = useState<any>(null);
  const [year, setYear] = useState<any>(new Date().getFullYear());
  const [month, setMonth] = useState<any>(new Date().getMonth() + 1);
  const dispatch = useAppDispatch();
  const holidays = useAppSelector(state => state.holidays.holidays);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const currentDate = new Date();
    const selectedDate = convertDate(currentDate)
    const eventsForDate = events.filter(event =>
      event.start.startsWith(selectedDate)
    );
    setCellDate(selectedDate);
    setSelectedDate(selectedDate.slice(-2));
    setSelectedEvents(eventsForDate);
  }, []);

  useEffect(() => {
    setIsLoading(true);
    reloadData();
  }, [month, year]);

  useEffect(() => {
    resetToDate(new Date(cellDate));
  }, [events]);

  function reloadData() {
    const dateSearch = getFirstAndLastDay(year, month);
    SchdService.getInstance().list(dateSearch).then(value => {
      if (value) {
        setEvents(value.data);
      }
    }).finally(() => setIsLoading(false));
  }

  function getFirstAndLastDay(year: number, month: number): { startYmd: string; endYmd: string } {
    // Ensure month is 1-indexed (1 for January, 12 for December)
    const firstDate = new Date(year, month - 1, 1); // First day of the month
    const lastDate = new Date(year, month, 0); // Last day of the month

    // Format the dates as yymmdd
    const formatDate = (date: Date): string => {
      const yyyy = date.getFullYear(); // Last two digits of the year
      const mm = String(date.getMonth() + 1).padStart(2, '0'); // Month (2 digits)
      const dd = String(date.getDate()).padStart(2, '0'); // Day (2 digits)
      return `${yyyy}${mm}${dd}`;
    };

    return {
      startYmd: formatDate(firstDate),
      endYmd: formatDate(lastDate),
    };
  }

  function parseYYYYMMDDWithMoment(dateStr: string): Date {
    // Use moment to parse and return a JavaScript Date object
    return moment(dateStr, "YYYYMMDD").toDate();
  }

  const handleDateClick = (dateClickInfo: any) => {
    const selectedDate = dateClickInfo.dateStr;
    const eventsForDate = events.filter(event =>
      convertDate(parseYYYYMMDDWithMoment(event.schdYmd)).startsWith(selectedDate)
    );

    setSelectedDate(selectedDate.slice(-2));
    setCellDate(selectedDate);
    setSelectedEvents(eventsForDate);
  };

  const renderDayCellContent = (args: any) => {
    const dateCheck = convertDate(args.date)
    const dayEvents = events.filter(value => convertDate(parseYYYYMMDDWithMoment(value.schdYmd)) === dateCheck);
    const eventCount = dayEvents.length;
    const holiday = holidays && Array.isArray(holidays) ? holidays.find((h: any) => h.crtrYmd === dateCheck.replaceAll('-', '')) : null;
    return (
      <>
        <div className="inner">
          <div className={`${cellDate === dateCheck ? 'selected' : ''} ${holiday?.hdayYn === 'Y' ? 'holiday' : ''}`}>{args.dayNumberText}</div>
          {holiday && (
            <div className="holiday-tit" title={holiday.hdayNm}>{holiday.hdayNm}</div>
          )}
        </div>
        {eventCount > 0 && (
          <div className="event">
            <div>{eventCount}</div>
          </div>
        )}
      </>
    );
  };

  const handlePrevMonth = () => {
    calendarApi && calendarApi.prev();
    changeMonth();

  };

  const handleNextMonth = () => {
    calendarApi && calendarApi.next();
    changeMonth();
  };

  const changeMonth = () => {
    const date = calendarApi.currentData.currentDate
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0');
    setMonth(month)
    setYear(year)
  }

  const handleTodayClick = () => {
    calendarApi.today();
    changeMonth();
    resetToDate(new Date());
  };

  function resetToDate(chosenDate: Date) {
    const selectedDate = convertDate(chosenDate);
    const eventsForDate = events.filter(event =>
      convertDate(parseYYYYMMDDWithMoment(event.schdYmd)).startsWith(selectedDate)
    );

    setSelectedDate(selectedDate.slice(-2));
    setCellDate(selectedDate);
    setSelectedEvents(eventsForDate);
  }

  function handleClickPopupRegist() {
    dispatch(openModal({
      path: '/dbUser/schedule',
      target: AddSche,
      wrapStyle: { minWidth: '30rem' },
      modalProps: {
        data: { event: {}, date: cellDate },
        onClose: closeDialog,
      }
    }));
  }

  function handleClickPopupUpdate(event: any) {
    dispatch(openModal({
      path: '/dbUser/schedule',
      target: AddSche,
      wrapStyle: { minWidth: '30rem' },
      modalProps: {
        data: { event, date: cellDate, mode: 'V' },
        onClose: closeDialog,
      }
    }));
  }

  function convertDate(date: any) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`
  }

  function closeDialog(data?: any) {
    if (data) {
      reloadData();
    }
  }

  return (
    <DashboardCard
      title="일정"
      isConfigMode={props.isConfigMode ?? false}
      moreButtonVisible={false}
      dashboardConfig={props.dashboardConfig}
    >
      <DashboardLoading isLoading={isLoading} />
      <div className="schedule-form">
        <div className="calendar">
          <div className="button-wrap">
            <button className="move-month prev" title="이전 달" onClick={handlePrevMonth}>
              <span className="sr-only">이전 달</span>
            </button>
            <div className="date">{year}년 <span className="month">{month}월</span></div>
            <button className="move-month next" title="다음 달" onClick={handleNextMonth}>
              <span className="sr-only">다음 달</span>
            </button>
            <button className="today" title="오늘" onClick={handleTodayClick}>
              <span>오늘</span>
              <span className="sr-only">오늘</span>
            </button>
          </div>
          <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            weekends={true}
            dateClick={handleDateClick}
            dayCellContent={renderDayCellContent}
            displayEventTime={false}
            displayEventEnd={false}
            dayHeaderContent={(args) => koreanDays[args.date.getUTCDay()]} // Customize day names to Korean
            headerToolbar={false}
            ref={(calendar) => setCalendarApi(calendar ? calendar.getApi() : null)}
          />
        </div>
        <div className="schedule-list">
          <div className="button-wrap" style={{ justifyContent: 'end' }}>
            <button title="일정 등록" onClick={handleClickPopupRegist}><span>일정 등록</span></button>
          </div>
          {selectedEvents.length > 0 ? (
            <ul>
              {selectedEvents.map((event: any, index: any) => (
                <li key={event.schdId}>
                  <Link to="#" onClick={event1 => handleClickPopupUpdate(event)}>
                    <div className="schedule-cont">
                      <div className="date"><em>{selectedDate}</em></div>
                      <span>{event.schdNm}<br></br>{event.schdExpl}</span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p>등록된 일정이 없습니다.</p>
          )}
        </div>
      </div>
    </DashboardCard>
  );
}
